/**
 * @file The web gallery cart reducer.
 */

import {
  DESELECT_DOWNLOAD,
  SELECT_PRINT,
  DESELECT_PRINT,
  SET_PRINT_QUANTITY,
  TOGGLE_WATERMARK,
  CHECKOUT_REQUEST,
  CHECKOUT_SUCCESS,
  CHECKOUT_FAILURE,
  RESET_DOWNLOADS,
  RESET_PRINTS,
  WEB_GALLERY_UPSELL_SEEN,
  SELECT_DOWNLOADS,
} from './web-gallery-cart-actions';
import { CART_ITEM_TYPES } from '../../constants/web-gallery-cart-constants';

const initialState = {
  // Both selectedDownloads and selectedPrints will hold
  // entire photoItems because they might belong to different
  // subalbums and even subfolders, meaning that there's no guarantee that
  // the current album have the photoId requested, or that the
  // subalbum has the photo requested.
  selectedDownloads: [], //
  // Prints consist of an object { photoId, printSize, quantity }
  selectedPrints: [],
  loading: false,
  error: '',
  success: false,
  // Temporary downloads array to avoid multiple purchase.
  purchasedDownloads: [],
  hasSeenUpsell: false,
};

const webGalleryCartReducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case SELECT_DOWNLOADS: {
      const cartItems = action.items.map(item => ({
        photoItem: item.photoItem,
        priceInfo: item.priceInfo,
        album: item.album,
        watermarkOption: true, // Asume this as true by default
        type: CART_ITEM_TYPES.DOWNLOAD,
      }));

      return {
        ...state,
        loading: false,
        error: '',
        success: false,
        selectedDownloads: [...state.selectedDownloads, ...cartItems],
      };
    }

    case DESELECT_DOWNLOAD:
      return {
        ...state,
        selectedDownloads: state.selectedDownloads.filter(
          cartItem =>
            cartItem.photoItem.id !== action.photoItem.id ||
            cartItem.album.id !== action.album.id,
        ),
      };

    case SELECT_PRINT: {
      const cartItem = {
        photoItem: action.photoItem,
        printSize: action.printSize,
        quantity: action.quantity || 1,
        priceInfo: action.priceInfo,
        album: action.album,
        watermarkOption: true, // Asume this as true by default
        type: CART_ITEM_TYPES.PRINT,
      };

      return {
        ...state,
        loading: false,
        error: '',
        success: false,
        selectedPrints: [...state.selectedPrints, cartItem],
      };
    }

    case DESELECT_PRINT:
      return {
        ...state,
        selectedPrints: state.selectedPrints.filter(
          cartItem =>
            cartItem.photoItem.id !== action.photoItem.id ||
            cartItem.printSize.id !== action.printSize.id ||
            cartItem.album.id !== action.album.id,
        ),
      };

    case SET_PRINT_QUANTITY: {
      const printData = {
        photoItem: action.photoItem,
        printSize: action.printSize,
        quantity: action.quantity || 1,
        album: action.album,
      };

      return {
        ...state,
        selectedPrints: state.selectedPrints.map(cartItem => {
          if (
            cartItem.photoItem.id !== printData.photoItem.id ||
            cartItem.printSize.id !== printData.printSize.id ||
            cartItem.album.id !== printData.album.id
          ) {
            return cartItem;
          }

          return {
            ...cartItem,
            quantity: action.quantity,
          };
        }),
      };
    }

    case TOGGLE_WATERMARK: {
      if (action.printSize) {
        // Is a print
        return {
          ...state,
          selectedPrints: state.selectedPrints.map(cartItem => {
            if (
              cartItem.photoItem.id !== action.photoItem.id ||
              cartItem.printSize.id !== action.printSize.id ||
              cartItem.album.id !== action.album.id
            ) {
              return cartItem;
            }

            return {
              ...cartItem,
              watermarkOption: !cartItem.watermarkOption,
            };
          }),
        };
      }

      return {
        // is a Download
        ...state,
        selectedDownloads: state.selectedDownloads.map(cartItem => {
          if (
            cartItem.photoItem.id !== action.photoItem.id ||
            cartItem.album.id !== action.album.id
          ) {
            return cartItem;
          }

          return {
            ...cartItem,
            watermarkOption: !cartItem.watermarkOption,
          };
        }),
      };
    }

    case RESET_DOWNLOADS:
      return {
        ...state,
        selectedDownloads: [],
      };

    case RESET_PRINTS:
      return {
        ...state,
        selectedPrints: [],
      };

    case CHECKOUT_REQUEST:
      return {
        ...state,
        loading: true,
        error: '',
        success: false,
      };

    case CHECKOUT_SUCCESS:
      return {
        ...state,
        loading: false,
        error: '',
        success: true,
        selectedDownloads: [],
        selectedPrints: [],
        purchasedDownloads: [
          ...state.purchasedDownloads,
          ...state.selectedDownloads.map(cartItem => ({
            photoId: cartItem.photoItem.id,
            albumId: cartItem.album.id,
          })),
        ],
      };

    case CHECKOUT_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error.message || action.error,
        success: false,
      };

    case WEB_GALLERY_UPSELL_SEEN:
      return {
        ...state,
        hasSeenUpsell: true,
      };

    default:
      return state;
  }
};

export { initialState };
export default webGalleryCartReducer;
